import { z } from 'zod'
import { logRequestFailure } from '@/helpers/request-outcome'
import { api } from '@/helpers/apiClient'
import { listQuery } from '@/lib/pagination'
import type { UserListQuery } from '@lib/input/user-list.input'

import {
  assignableRolesDtoSchema,
  type AssignableRolesDto,
  managedVenuesDtoSchema,
  userDtoSchema,
  userListDtoSchema,
  type ManagedVenuesDto,
  type UserDto,
  type UserListDto,
} from '@lib/dto/user.dto'
import type { InviteUserInput, SetManagedVenuesInput, UpdateUserInput } from '@lib/input/user.input'
import type { IUserService } from '@/types/UserService.types'

/**
 * Service for the tenant-admin user endpoints. Responses are validated against
 * the shared `@lib` schemas at the boundary; results are tenant-scoped by the API.
 */
class UserService implements IUserService {
  async list(query: UserListQuery = {}, page?: number): Promise<UserListDto> {
    try {
      return await api.get(`/users${listQuery({ ...query }, page)}`, userListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list users', {}, error)
      throw error
    }
  }

  async invite(input: InviteUserInput): Promise<UserDto> {
    try {
      return await api.post('/users', input, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to invite user', {}, error)
      throw error
    }
  }

  async update(id: string, input: UpdateUserInput): Promise<UserDto> {
    try {
      return await api.patch(`/users/${id}`, input, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update user', { id }, error)
      throw error
    }
  }

  async deletePending(id: string): Promise<void> {
    try {
      await api.del(`/users/${id}`)
    } catch (error) {
      logRequestFailure('Failed to delete invited user', { id }, error)
      throw error
    }
  }

  async deactivate(id: string): Promise<UserDto> {
    try {
      return await api.post(`/users/${id}/deactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate user', { id }, error)
      throw error
    }
  }

  async reactivate(id: string): Promise<UserDto> {
    try {
      return await api.post(`/users/${id}/reactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reactivate user', { id }, error)
      throw error
    }
  }

  async resendInvite(id: string): Promise<void> {
    try {
      await api.post(`/users/${id}/resend-invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to resend invitation', { id }, error)
      throw error
    }
  }

  async listManagedVenues(userId: string): Promise<ManagedVenuesDto> {
    try {
      return await api.get(`/users/${userId}/managed-venues`, managedVenuesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list managed venues', { userId }, error)
      throw error
    }
  }

  async setManagedVenues(userId: string, input: SetManagedVenuesInput): Promise<ManagedVenuesDto> {
    try {
      return await api.post(`/users/${userId}/managed-venues`, input, managedVenuesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set managed venues', { userId }, error)
      throw error
    }
  }

  async assignableRoles(): Promise<AssignableRolesDto> {
    try {
      return await api.get('/users/roles', assignableRolesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the roles to give', {}, error)
      throw error
    }
  }
}

export const userService = new UserService()
